import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { api, videoApi } from '@carrent/api'
import { Icon, PageHeader, StatusBadge } from '@carrent/ui'
import { useAuthStore } from '@carrent/core'
import { useVideoSettings } from '@carrent/domain'

import TripActions from './TripActions'

/**
 * «Видеоосмотр» — звонок, привязанный к поездке: диспетчер смотрит
 * машину камерой водителя. Кнопки нет (не серая, а отсутствует),
 * если модуль/интеграция выключены или роль не создаёт звонки.
 */
function VideoInspectionButton({ trip }) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { settings, canCreate } = useVideoSettings()
  const [busy, setBusy] = useState(false)

  if (!settings.enabled || !settings.call_from_trip || !canCreate(user?.role)) {
    return null
  }

  const start = async () => {
    setBusy(true)
    try {
      const car = trip.car || {}
      const { data } = await videoApi.create({
        title: `Видеоосмотр: ${car.brand || ''} ${car.model || ''} №${trip.id}`.trim(),
        privacy: 'open',
        trip_id: trip.id,
        participant_ids: trip.user?.id ? [trip.user.id] : [],
      })
      navigate(`/admin/video/${data.data.id}`)
    } catch {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      className="btn btn--outline btn--sm"
      onClick={start}
      disabled={busy}
      title="Видеозвонок с водителем, привязанный к этой поездке"
    >
      {busy ? <span className="spinner spinner--sm" /> : <Icon name="chat" size={14} />}
      Видеоосмотр
    </button>
  )
}
import TripComparison from './TripComparison'
import TripTrack from './TripTrack'

/**
 * Карточка поездки в консоли.
 *
 * ── Почему страница устроена в две колонки ────────────────────────
 *
 * Прежняя версия была лентой одинаковых карточек в одну колонку:
 * фото выезда, фото возврата, события, кнопка. На телефоне это
 * читается, на рабочем мониторе диспетчера — нет: он прокручивает
 * экран, чтобы сверить пробег с фото одометра, потом обратно, чтобы
 * нажать «подтвердить». Решение принимается по данным, которые
 * не помещаются в один взгляд.
 *
 * Теперь слева доказательства (сравнение актов, схема повреждений),
 * справа — липкая колонка решения: паспорт поездки, события и сами
 * действия. Прокручивая доказательства, диспетчер не теряет из виду
 * ни цифры, ни кнопку.
 *
 * На узком экране колонки складываются, и решение идёт первым:
 * иначе до кнопки пришлось бы пролистать все снимки.
 */

function formatDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru', { dateStyle: 'medium', timeStyle: 'short' })
}

function formatKm(value) {
  return value ? `${value.toLocaleString('ru')} км` : '—'
}

/** Строка паспорта поездки. */
function Row({ label, children }) {
  return (
    <div className="trip-facts__row">
      <dt className="trip-facts__label">{label}</dt>
      <dd className="trip-facts__value">{children}</dd>
    </div>
  )
}

/**
 * Плановое время завершения рейса.
 *
 * Пока плана нет, оперативный контроль оценивает отклонение по средней
 * длительности — цифра приблизительная и помечена знаком ≈. План
 * превращает её в настоящее опоздание, поэтому ставится здесь,
 * прямо в карточке, а не отдельной формой.
 *
 * Показывается только у идущего рейса: у завершённого план менять
 * поздно, у него уже есть фактическое время.
 */
function PlannedEnd({ trip, onChanged }) {
  const [value, setValue] = useState(() => toLocalInput(trip.planned_end_at))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  if (trip.status !== 'active') return null

  const save = async (next) => {
    setSaving(true)
    setError(null)
    try {
      await api.patch(`/admin/trips/${trip.id}/plan`, {
        planned_end_at: next ? new Date(next).toISOString() : null,
      })
      setValue(next)
      onChanged?.()
    } catch (e) {
      setError(e?.response?.data?.message || 'План не сохранился')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card trip-side__block">
      <p className="trip-side__title">Плановое завершение</p>

      <input
        className="input"
        type="datetime-local"
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
      />

      <div className="trip-plan__actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={saving || !value || value === toLocalInput(trip.planned_end_at)}
          onClick={() => save(value)}
        >
          Сохранить
        </button>
        {trip.planned_end_at && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={saving}
            onClick={() => save('')}
          >
            Снять план
          </button>
        )}
      </div>

      {error && <p className="field__hint field__hint--warn">{error}</p>}

      <p className="trip-plan__hint">
        {trip.planned_end_at
          ? 'Оперативный контроль считает опоздание от этого времени.'
          : 'Без плана отклонение оценивается по средней длительности рейса.'}
      </p>
    </div>
  )
}

/** datetime-local понимает только местное время без зоны. */
function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function EventList({ events }) {
  if (!events.length) {
    return <p className="trip-side__empty">За поездку событий не вносили.</p>
  }

  return (
    <ul className="trip-events">
      {events.map((ev) => (
        <li key={ev.id} className="trip-events__item">
          <div className="trip-events__main">
            <p className="trip-events__label">{ev.event_label}</p>
            {ev.description && (
              <p className="trip-events__description">{ev.description}</p>
            )}
            <p className="trip-events__figures">
              {ev.amount ? (
                <span className="trip-events__amount">
                  {ev.amount.toLocaleString('ru')} ₽
                </span>
              ) : null}
              {ev.fuel_liters ? (
                <span className="text-muted">{ev.fuel_liters} л · {ev.fuel_type}</span>
              ) : null}
            </p>
          </div>
          {ev.files?.length > 0 && (
            <div className="trip-events__files">
              {ev.files.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={`Фото события ${i + 1}`} loading="lazy" />
                </a>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Действия, доступные прямо из сравнения. */
function Decision({ trip, onConfirmed, onMessage, onChanged }) {
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(null)

  const confirm = async () => {
    setBusy('confirm')
    try {
      await api.post(`/trips/${trip.id}/confirm`, { comment })
      onMessage({ type: 'success', text: 'Поездка подтверждена' })
      onConfirmed()
    } catch (e) {
      onMessage({ type: 'error', text: e.response?.data?.message || 'Не удалось подтвердить' })
    } finally {
      setBusy(null)
    }
  }

  // Ремонт заводится со ссылкой на поездку: без неё механик получает
  // задачу «посмотреть кузов» без повода.
  const createRepair = async () => {
    setBusy('repair')
    try {
      const { data } = await api.post(`/admin/cars/${trip.car?.id}/repairs`, {
        title: `Осмотр после поездки №${trip.id}`,
        repair_type: 'body',
        description: `Заведено из сравнения актов поездки №${trip.id}.`,
        mileage_at: trip.end_mileage || undefined,
      })
      const repairId = data?.data?.id
      onMessage({
        type: 'success',
        text: repairId ? `Ремонт №${repairId} создан` : 'Ремонт создан',
      })
    } catch (e) {
      onMessage({ type: 'error', text: e.response?.data?.message || 'Не удалось создать ремонт' })
    } finally {
      setBusy(null)
    }
  }

  const pending = trip.status === 'finished_by_user'

  return (
    <div className="trip-decision">
      {pending && (
        <div className="field">
          <label className="field__label" htmlFor="admin-comment">
            Комментарий администратора
          </label>
          <textarea
            id="admin-comment"
            className="input trip-decision__comment"
            rows={2}
            placeholder="Необязательно"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
      )}

      {pending && (
        <button type="button" className="btn btn--primary btn--full"
          onClick={confirm} disabled={busy !== null}>
          {busy === 'confirm' ? <span className="spinner" /> : <Icon name="check" size={16} />}
          Подтвердить завершение
        </button>
      )}

      {trip.car?.id && (
        <button type="button" className="btn btn--secondary btn--full"
          onClick={createRepair} disabled={busy !== null}>
          {busy === 'repair' ? <span className="spinner" /> : <Icon name="wrench" size={16} />}
          Создать ремонт
        </button>
      )}

      <TripActions trip={trip} onMessage={onMessage} onChanged={onChanged} />

      {trip.status === 'confirmed_by_admin' && trip.confirmed_at && (
        <p className="trip-decision__done">
          Подтверждено {formatDateTime(trip.confirmed_at)}
        </p>
      )}
    </div>
  )
}

/** Соседние поездки этой машины: закрывает вопрос «а не до меня ли». */
function Neighbours({ trip }) {
  const [trips, setTrips] = useState(null)

  useEffect(() => {
    if (!trip.car?.id) return undefined
    let alive = true
    api.get(`/trips/admin/by-car/${trip.car.id}`, { params: { per_page: 50 } })
      .then(({ data }) => { if (alive) setTrips(data.data?.items || []) })
      .catch(() => { if (alive) setTrips([]) })
    return () => { alive = false }
  }, [trip.car?.id])

  if (!trips?.length) return null

  // Список приходит от новых к старым.
  const index = trips.findIndex((t) => t.id === trip.id)
  if (index === -1) return null
  const newer = trips[index - 1]
  const older = trips[index + 1]
  if (!newer && !older) return null

  return (
    <>
      <p className="trip-side__title">Соседние поездки машины</p>
      <p className="trip-side__note">
        Повреждение могло появиться не в этой поездке — рядом видно, кто брал
        машину до и после.
      </p>
      <div className="trip-neighbours">
        {older ? (
          <Link className="btn btn--ghost btn--sm" to={`/admin/trips/${older.id}`}>
            <Icon name="chevronLeft" size={14} />
            Предыдущая
          </Link>
        ) : <span className="trip-side__empty">Предыдущей нет</span>}
        {newer ? (
          <Link className="btn btn--ghost btn--sm" to={`/admin/trips/${newer.id}`}>
            Следующая
            <Icon name="chevronRight" size={14} />
          </Link>
        ) : <span className="trip-side__empty">Следующей нет</span>}
      </div>
    </>
  )
}

export default function AdminTripDetail() {
  const { id } = useParams()
  const [trip, setTrip] = useState(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [msg, setMsg] = useState(null)
  // Меняется после подтверждения: сравнение пересчитывает флаги.
  const [revision, setRevision] = useState(0)

  const load = () => {
    setLoading(true)
    setFailed(false)
    api.get(`/admin/trips/${id}`)
      .then((r) => setTrip(r.data?.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }
  useEffect(load, [id])

  if (loading) {
    return (
      <div>
        <PageHeader title="Поездка" backTo="/admin/trips" />
        <div className="trip-page__state" role="status">
          <span className="spinner" />
          <span className="text-muted">Загружаем поездку…</span>
        </div>
      </div>
    )
  }

  if (failed || !trip) {
    return (
      <div>
        <PageHeader title="Поездка" backTo="/admin/trips" />
        <div className="trip-page__state state">
          <p className="state__title">Поездка не открылась</p>
          <p className="state__text">
            {failed ? 'Сервер не ответил.' : 'Такой поездки нет или доступ закрыт.'}
          </p>
          {failed && (
            <button type="button" className="btn btn--secondary" onClick={load}>
              Повторить
            </button>
          )}
        </div>
      </div>
    )
  }

  const car = trip.car || {}
  const user = trip.user || {}
  const events = trip.events || []
  const extras = (trip.photos || []).filter((p) => p.category === 'extra')

  return (
    <div className="trip-page">
      <PageHeader
        title={`${car.brand || ''} ${car.model || ''} №${trip.id}`.trim()}
        backTo="/admin/trips"
        right={(
          <span className="flex-row gap-sm video-actions">
            <VideoInspectionButton trip={trip} />
            <StatusBadge status={trip.status} />
          </span>
        )}
      />

      <div className="p-page trip-page__grid">
        <main className="trip-page__main">
          {msg && (
            <div className={`alert alert--${msg.type === 'success' ? 'success' : 'error'}`}
              role="status">
              {msg.text}
            </div>
          )}

          {trip.has_damage && (
            <div className="alert alert--error">
              <Icon name="warning" size={16} />
              Водитель сообщил о повреждениях — требуется осмотр.
            </div>
          )}

          <TripComparison trip={trip} onChanged={revision} />

          <p className="section-title">Где была машина</p>
          <TripTrack
            track={trip.track}
            startLocation={trip.start_location}
            endLocation={trip.end_location}
          />

          {extras.length > 0 && (
            <>
              <p className="section-title">Дополнительные снимки</p>
              <div className="compare__only-grid">
                {extras.map((photo) => (
                  <figure key={photo.id} className="compare__act-shot">
                    <img src={photo.url} alt={photo.label || 'Дополнительный снимок'}
                      loading="lazy" />
                    <figcaption><span>{photo.label || 'Доп.'}</span></figcaption>
                  </figure>
                ))}
              </div>
            </>
          )}
        </main>

        <aside className="trip-page__side">
          <div className="card trip-side__block">
            <p className="trip-side__title">Решение</p>
            <Decision
              trip={trip}
              onMessage={setMsg}
              onChanged={() => setRevision((r) => r + 1)}
              onConfirmed={() => { setRevision((r) => r + 1); load() }}
            />
          </div>

          <PlannedEnd trip={trip} onChanged={load} />

          <div className="card trip-side__block">
            <p className="trip-side__title">Поездка</p>
            <dl className="trip-facts">
              <Row label="Водитель">
                {user.full_name || '—'}
                {user.phone && <span className="text-muted"> · {user.phone}</span>}
              </Row>
              <Row label="Машина">
                {`${car.brand || ''} ${car.model || ''}`.trim() || '—'}
                {car.plate && <span className="text-muted"> · {car.plate}</span>}
              </Row>
              <Row label="Начало">{formatDateTime(trip.started_at)}</Row>
              {trip.planned_end_at && (
                <Row label="План">{formatDateTime(trip.planned_end_at)}</Row>
              )}
              <Row label="Конец">{formatDateTime(trip.finished_at)}</Row>
              <Row label="Пробег">
                {formatKm(trip.start_mileage)} → {formatKm(trip.end_mileage)}
              </Row>
            </dl>
          </div>

          {(trip.start_comment || trip.end_comment) && (
            <div className="card trip-side__block">
              <p className="trip-side__title">Комментарии водителя</p>
              {trip.start_comment && (
                <p className="trip-side__comment">
                  <span className="trip-side__comment-phase">При выезде</span>
                  {trip.start_comment}
                </p>
              )}
              {trip.end_comment && (
                <p className="trip-side__comment">
                  <span className="trip-side__comment-phase">При возврате</span>
                  {trip.end_comment}
                </p>
              )}
            </div>
          )}

          <div className="card trip-side__block">
            <p className="trip-side__title">События ({events.length})</p>
            <EventList events={events} />
          </div>

          <div className="card trip-side__block">
            <Neighbours trip={trip} />
          </div>
        </aside>
      </div>
    </div>
  )
}
